import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Clock, User as UserIcon } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { API } from '@/App';
import { formatDate } from '@/lib/dateUtils';

const LAST_SEEN_KEY = 'pollen_notif_last_seen';
const POLL_MS = 60_000; // poll every 60s

const authHeaders = () => {
  const t = localStorage.getItem('session_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const wrapRef = useRef(null);

  const fetchNotifs = useCallback(async () => {
    try {
      const res = await fetch(`${API}/pollen/me/notifications?limit=30`, { credentials: 'include', headers: authHeaders() });
      if (!res.ok) return;
      const d = await res.json();
      const items = d.notifications || [];
      setNotifications(items);
      const lastSeen = localStorage.getItem(LAST_SEEN_KEY) || '';
      const count = items.filter(n => (n.timestamp || '') > lastSeen).length;
      setUnread(count);
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    fetchNotifs();
    const id = setInterval(fetchNotifs, POLL_MS);
    return () => clearInterval(id);
  }, [fetchNotifs]);

  useEffect(() => {
    const onClickOut = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClickOut);
    return () => document.removeEventListener('mousedown', onClickOut);
  }, []);

  const markAllRead = () => {
    const latest = notifications[0]?.timestamp || new Date().toISOString();
    localStorage.setItem(LAST_SEEN_KEY, latest);
    setUnread(0);
  };

  const handleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next) markAllRead();
  };

  const goTo = (n) => {
    setOpen(false);
    const t = n.entity_type;
    const id = n.entity_id;
    if (t === 'tour' && id) navigate(`/pollen-dots/tour/${id}`);
    else if (t === 'vendor' && id) navigate(`/pollen-dots/vendor/${id}`);
    else if (t === 'customer' && id) navigate(`/pollen-dots/customer/${id}`);
    else if (t === 'team' && id) navigate(`/pollen-dots/team/${id}`);
    else if (t === 'query' && id) navigate(`/pollen-dots/queries/${id}`);
    else if (t === 'location') navigate('/pollen-dots/locations');
    else if (t === 'data-gap-task') {
      // For auto-closed data-gap tasks, send the supervisor to their own
      // Pulse with the Done tab pre-opened so they can audit immediately.
      navigate('/pollen-dots/profile?datagaps=done');
    }
  };

  const actionColor = (a) => {
    if (a === 'created' || a === 'auto_created') return 'bg-emerald-100 text-emerald-800';
    if (a === 'updated') return 'bg-blue-100 text-blue-800';
    if (a === 'deleted') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="relative" ref={wrapRef}>
      <Button
        variant="ghost"
        size="sm"
        className="relative p-2 h-9 w-9"
        onClick={handleOpen}
        data-testid="notification-bell-btn"
        aria-label="Notifications">
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-rose-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center" data-testid="notif-unread-count">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 mt-2 w-[340px] max-w-[92vw] bg-white border rounded-xl shadow-xl z-50" data-testid="notif-dropdown">
          <div className="flex items-center justify-between px-3 py-2 border-b">
            <span className="font-semibold text-sm">Team Activity</span>
            <Button variant="ghost" size="sm" className="text-xs h-7" onClick={markAllRead}>Mark all read</Button>
          </div>
          <div className="max-h-[480px] overflow-y-auto">
            {notifications.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">No recent activity.</p>
            ) : notifications.map((n, i) => {
              const isUnread = (n.timestamp || '') > (localStorage.getItem(LAST_SEEN_KEY) || '');
              return (
                <button
                  type="button"
                  key={i}
                  onClick={() => goTo(n)}
                  className={`w-full text-left px-3 py-2 border-b last:border-0 hover:bg-muted transition-colors ${isUnread ? 'bg-primary/5' : ''}`}
                  data-testid={`notif-item-${i}`}>
                  <div className="flex items-start gap-2">
                    <div className="mt-0.5">
                      <Badge className={`text-[10px] h-4 px-1.5 ${actionColor(n.action)}`}>{n.action}</Badge>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium capitalize">{n.entity_type}</p>
                      <p className="text-[11px] text-muted-foreground line-clamp-2">{n.details || '—'}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><UserIcon className="w-2.5 h-2.5" />{(n.user_email || '').split('@')[0]}</span>
                        <span className="text-[10px] text-muted-foreground flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" />{relTime(n.timestamp)}</span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

function relTime(iso) {
  if (!iso) return '';
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
    return formatDate(iso.slice(0, 10));
  } catch { return ''; }
}

export default NotificationBell;
